import React, { useState, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// 设置PDF.js worker - 使用本地文件
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

interface PDFViewerProps {
  url: string;
}

const PDFViewer: React.FC<PDFViewerProps> = ({ url }) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setLoading(false);
    setError(null);
  };

  const onDocumentLoadError = (error: Error) => {
    setError(`PDF加载失败: ${error.message}`);
    setLoading(false);
    console.error('PDF加载错误:', error);
  };

  const goToPrevPage = () => {
    setPageNumber(prev => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber(prev => Math.min(prev + 1, numPages));
  };

  const zoomIn = () => {
    setScale(prev => Math.min(prev + 0.2, 3.0));
  };

  const zoomOut = () => {
    setScale(prev => Math.max(prev - 0.2, 0.5));
  };

  const resetZoom = () => {
    setScale(1.0);
  };

  useEffect(() => {
    setPageNumber(1);
    setScale(1.0);
    setLoading(true);
    setError(null);
  }, [url]);

  if (error) {
    return (
      <div className="pdf-error">
        <p>{error}</p>
        <p>请检查：</p>
        <ul>
          <li>PDF文件是否存在于 public/pdfs/ 目录中</li>
          <li>文件名是否与代码中的路径匹配</li>
          <li>PDF文件是否损坏</li>
        </ul>
      </div>
    );
  }

  return (
    <div className="pdf-viewer">
      <div className="pdf-controls">
        <div className="page-controls">
          <button 
            onClick={goToPrevPage} 
            disabled={pageNumber <= 1}
            className="control-button"
          >
            上一页
          </button>
          <span className="page-info">
            第 {pageNumber} 页，共 {numPages} 页
          </span>
          <button 
            onClick={goToNextPage} 
            disabled={pageNumber >= numPages}
            className="control-button"
          >
            下一页
          </button>
        </div>
        
        <div className="zoom-controls">
          <button onClick={zoomOut} className="control-button">缩小</button>
          <span className="zoom-info">{Math.round(scale * 100)}%</span>
          <button onClick={zoomIn} className="control-button">放大</button>
          <button onClick={resetZoom} className="control-button">重置</button>
        </div>
      </div>

      <div className="pdf-container">
        {loading && (
          <div className="loading">
            <p>正在加载PDF...</p>
          </div>
        )}
        
        <Document
          file={url}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={<div className="loading">正在加载PDF...</div>}
          error={<div className="error">PDF加载失败</div>}
        >
          <Page
            pageNumber={pageNumber}
            scale={scale}
            renderTextLayer={true}
            renderAnnotationLayer={true}
          />
        </Document>
      </div>
    </div>
  );
};

export default PDFViewer;
